
import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { KBD } from "../utils/Keybindings";
import { listen } from "../hooks/Listener";

const ScrollWrap = forwardRef(({ controller = "", route = "", fillHeight = false, children, allow_nudge = false, width = false, height = "inherit", columnView = false, reset = "", style = {} }, ref) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [enabled, setEnabled] = useState(true);


  const nudge = (amount = 50) => {

    if (!allow_nudge) {
      return;
    }

    const c = containerRef.current;
    if (!c) return;

    c.scrollBy({
      left: amount,
      behavior: "smooth"
    });
  };


  // Update thumb size and position
  const updateThumb = () => {
    const c = containerRef.current;
    if (!c) return;
    const trackWidth = trackRef.current.clientWidth;

    if (c.scrollWidth <= c.clientWidth) {
      setEnabled(false);
      setThumbWidth(trackWidth);
      setThumbLeft(0);
    } else {
      setEnabled(true);
      const ratio = c.clientWidth / c.scrollWidth;
      const newThumbWidth = Math.max(ratio * trackWidth, 20); // minimum width
      const newThumbLeft = (c.scrollLeft / (c.scrollWidth - c.clientWidth)) * (trackWidth - newThumbWidth);
      setThumbWidth(newThumbWidth);
      setThumbLeft(newThumbLeft);
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;
    updateThumb();
  }, [reset]);


  // Expose API to parent
  useImperativeHandle(ref, () => ({
    scrollToElement(el) {
      if (!el || !containerRef.current) return;
      const c = containerRef.current;

      const elLeft = el.offsetLeft;
      const elRight = elLeft + el.offsetWidth;
      const viewLeft = c.scrollLeft;
      const viewRight = viewLeft + c.clientWidth;

      if (elLeft < viewLeft) {
        // element is to the left → scroll left
        c.scrollLeft = elLeft;
      } else if (elRight > viewRight) {
        // element is to the right → scroll right
        c.scrollLeft = elRight - c.clientWidth;
      }
    }
  }));

  useEffect(() => {


    if (!width) {
      return;
    }

    const c = containerRef.current;
    if (!c) return;
    c.addEventListener("scroll", updateThumb);
    window.addEventListener("resize", updateThumb);
    updateThumb();
    return () => {
      c.removeEventListener("scroll", updateThumb);
      window.removeEventListener("resize", updateThumb);
    };
  }, [width, columnView]);

  useEffect(() => {

    if (width) {
      return;
    }

    const c = containerRef.current;
    if (!c) return;

    // update on scroll
    c.addEventListener("scroll", updateThumb);

    // update on container resize
    const resizeObserver = new ResizeObserver(() => updateThumb());
    resizeObserver.observe(c);

    // also listen for window resize (safety)
    window.addEventListener("resize", updateThumb);

    // run once
    updateThumb();

    return () => {
      c.removeEventListener("scroll", updateThumb);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateThumb);
    };
  }, []);



  // Drag the thumb
  const onThumbMouseDown = (e) => {
    if (!enabled) return; // ignore drag if disabled
    e.stopPropagation();
    const startX = e.pageX;
    const startLeft = thumbLeft;
    const trackWidth = trackRef.current.clientWidth;
    const maxThumbLeft = trackWidth - thumbWidth;

    const onMove = (eMove) => {
      const delta = eMove.pageX - startX;
      const newLeft = Math.max(0, Math.min(startLeft + delta, maxThumbLeft));
      setThumbLeft(newLeft);
      const ratio = newLeft / maxThumbLeft;
      containerRef.current.scrollLeft = ratio * (containerRef.current.scrollWidth - containerRef.current.clientWidth);
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };


  useEffect(() => {
    const actions = [
      { key: KBD.SHIFT_ARROW_LEFT, action: (e) => nudge(-100) },
      { key: KBD.SHIFT_ARROW_RIGHT, action: (e) => nudge(100) },
    ];
    const cleanup = listen(controller, route, actions, (e) => {

      if (e?.shiftKey) {
        nudge(e.deltaY)
      }


    });
    return cleanup;
  }, [])



  return (
    <div style={{ ...style, userSelect: "none", flex: width ? "1" : "inherit", minWidth: width ? "0" : "inherit", height: fillHeight ? "auto" : height, ...(fillHeight && { display: "flex", flexDirection: "column", flex: "1 1 0", minHeight: 0, }), }}>
      {/* Scrollable content */}
      <div ref={containerRef} style={{ overflow: "hidden", whiteSpace: "nowrap", height: fillHeight ? "auto" : height, ...(fillHeight && { flex: "1 1 0", minHeight: 0, }), }} >
        <div style={{ display: width ? "inherit" : "inline-block", minWidth: width ? "inherit" : "max-content", height: fillHeight ? "100%" : height, }}>
          {children}
        </div>

      </div>

      {/* Synthetic scrollbar */}
      <div
        ref={trackRef}
        style={{
          position: "relative",
          height: "7px",
          background: "transaprent",
          marginTop: "6px",
          borderRadius: "10px",
          flexShrink : "0"
        }}
      >
        <div
          onMouseDown={onThumbMouseDown}
          style={{
            position: "absolute",
            height: "100%",
            background: "var(--text-light)",
            borderRadius: "10px",
            width: `${thumbWidth}px`,
            left: `${thumbLeft}px`,
            cursor: enabled ? "pointer" : "default",
            opacity: enabled ? 1 : 0.1
          }}
        />
      </div>
    </div>
  );
});

export default ScrollWrap;

